import { useAuth0 } from "@auth0/auth0-react";
import { useState, useContext } from 'react';
import { FinicityConnect } from '@finicity/connect-web-sdk';
import { OBCustomerContext } from '../contexts/ob-customer-context';
import { getUTCTimetamp } from "../utilities/date-utils";
import OpenBankingTransactions from "../components/OpenBanking/OpenBankingTransactions";
import {
    generateAppToken,
    getInternalOBCustomerAccounts
} from "../utilities/ob-utils";

import OBAccountPreview from "../components/Account/OBAccountPreview";

import {
    svc_ob_genConnectURL,
    svc_ob_getCustomerAccounts,
    svc_ob_getCustomerTransactions,
    svc_ob_refreshCustomerAccounts,
} from '../services/external-openbanking.service';

import {
    svc_createOBCustomerAccount,
    svc_updateOBCustomerAccount
} from '../services/internal-openbanking.service';

import LoadingSpinner from "../components/ui/LoadingSpinner";

const OpenBankingIntegration = () => {
    const [isFetchingAccounts, setIsFetchingAccounts] = useState(false);
    const [isFetchingTransactions, setIsFetchingTransactions] = useState(false);
    const [expandAll, setExpandAll] = useState(false);
    const { user, getAccessTokenSilently } = useAuth0();
    const {
        obAppToken,
        setOBAppToken,
        obCustomer,
        updateOBCustomer
    } = useContext(OBCustomerContext);

    const [customerTransactions, setCustomerTransactions] = useState([]);
    const [obCustomerAccounts, setOBCustomerAccounts] = useState([]);

    let connectUrl = '';

    const handleConnectClick = async (e) => {
        e.preventDefault();

        if (obAppToken && obCustomer) {
            generateConnectUrl(obAppToken, obCustomer);
        }
    }

    const handleRefreshAccountsClick = async (e) => {
        e.preventDefault();
        setIsFetchingAccounts(true);
        getAccounts(obCustomer?.obId, true);
    }

    const handleGetTransactionsClick = async (e) => {
        e.preventDefault();
        setIsFetchingTransactions(true);
        getTransactions(obCustomer?.obId, obAppToken);
    }

    const generateConnectUrl = async (token, customer) => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await svc_ob_genConnectURL(accessToken, token, customer?.obId);

        if (error) {
            if (parseInt(error.statusCode) === 401) {
                const updatedToken = await generateAppToken(accessToken);
                setOBAppToken(updatedToken);
                updateOBCustomer(updatedToken);
                await generateConnectUrl(updatedToken, customer);
                return;
            }

            console.error('error', error);
            throw new Error(error);
        }

        connectUrl = data.link;

        if (typeof window !== 'undefined') {
            const connectEventHandlers = {
                onDone: (event) => {
                    console.log('onDone', event);
                },
                onCancel: (event) => {
                    console.log('onCancel', event);
                },
                onError: (event) => {
                    console.log('onError', event);
                },
                onUser: ({ action, code, customerId }) => {
                    if (
                        action === 'End' &&
                        code === 200 &&
                        customerId
                    ) {
                        console.log('customerId', customerId);
                        getAccounts(customerId)
                            .then(async () => {
                                const accessToken = await getAccessTokenSilently();

                                const onFailure = async (error) => {
                                    if (parseInt(error.statusCode) === 401) {
                                        const accessToken = await getAccessTokenSilently();
                                        const updatedToken = await generateAppToken(accessToken);
                                        setOBAppToken(updatedToken);
                                        updateOBCustomer(updatedToken);
                                        getInternalOBCustomerAccounts(obCustomer, accessToken, updatedToken, user.sub, setOBCustomerAccounts);
                                        return;
                                    }
                                }

                                getInternalOBCustomerAccounts(
                                    obCustomer,
                                    accessToken,
                                    user.sub,
                                    setOBCustomerAccounts,
                                    onFailure
                                )
                            });
                    }
                },
                onLoad: () => {
                    console.log('loaded');
                },
            };

            const connectOptions = {
                overlay: 'rgba(199,201,199, 0.5);z-index: 10000;',
            };

            FinicityConnect.launch(
                connectUrl,
                connectEventHandlers,
                connectOptions
            );
        }
    };

    const getAccounts = async (
        customerId,
        refresh = false,
        appToken = obAppToken
    ) => {
        const getAccountsMethod = (
            refresh
                ? svc_ob_refreshCustomerAccounts
                : svc_ob_getCustomerAccounts
        );
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getAccountsMethod(accessToken, appToken, customerId);

        if (error) {
            // Retry with new token if 401
            if (parseInt(error.statusCode) === 401) {
                const updatedToken = await generateAppToken(accessToken);
                setOBAppToken(updatedToken);
                updateOBCustomer(updatedToken);
                getAccounts(customerId, refresh, updatedToken);
                return;
            }

            console.error('error', error);
            setIsFetchingAccounts(false);
            throw new Error(error);
        }

        // Update or create accounts
        const freshAccounts = data.accounts;
        createOrUpdateAccounts(freshAccounts);
    };

    const createOrUpdateAccounts = async (freshAccounts) => {
        const accessToken = await getAccessTokenSilently();
        const currentAccounts = await getInternalOBCustomerAccounts(
            obCustomer,
            accessToken,
            user.sub
        );
        const accountsToCreate = [];
        const accountsToUpdate = [];

        freshAccounts.forEach((freshAccount) => {
            const existingAccount = currentAccounts.find(
                (obAccount) => obAccount.accountId === parseInt(freshAccount.id)
            );

            if (existingAccount) {
                accountsToUpdate.push({
                    existingAccount,
                    freshAccount
                });
            } else {
                accountsToCreate.push(freshAccount);
            }
        });

        const createPromises = accountsToCreate.map((account) => {
            return svc_createOBCustomerAccount(accessToken, user.sub, {
                obCustomerId: obCustomer.id,
                accountId: parseInt(account.id),
                data: account,
                includeBalance: false,
            });
        });

        const updatePromises = accountsToUpdate.map((account) => {
            return svc_updateOBCustomerAccount(accessToken, user.sub, {
                obCustomerId: obCustomer.id,
                accountId: parseInt(account.existingAccount.accountId),
                data: account.freshAccount,
                includeBalance: account.existingAccount.includeBalance,
            });
        });

        await Promise.all(createPromises);
        await Promise.all(updatePromises);
        await getInternalOBCustomerAccounts(obCustomer, accessToken, user.sub, setOBCustomerAccounts);

        setIsFetchingAccounts(false);
    };

    const getTransactions = async (
        customerId,
        token = obAppToken,
        dateFrom = getUTCTimetamp(new Date(2024, 8, 1)),
        dateTo = Math.floor(Date.now() / 1000)
    ) => {
        const fromDate = dateFrom;
        const toDate = dateTo;
        const accessToken = await getAccessTokenSilently();

        const { data, error } = await svc_ob_getCustomerTransactions(accessToken, token, customerId, {
            fromDate,
            toDate,
        });

        if (error) {
            // Retry with new token if 401
            if (parseInt(error.statusCode) === 401) {
                const accessToken = await getAccessTokenSilently();
                const updatedToken = await generateAppToken(accessToken);
                setOBAppToken(updatedToken);
                updateOBCustomer(updatedToken);
                getTransactions(customerId, updatedToken);
                return;
            }

            console.error('error', error);
            setIsFetchingTransactions(false);
            throw new Error(error);
        }

        setCustomerTransactions(data.transactions);
        setIsFetchingTransactions(false);
    };

    return (
        <>
            {obCustomer && (
                <div style={{
                    marginBottom: '2rem',
                    backgroundColor: "#fff",
                    padding: '1rem',
                }}>
                    <h2>Connected Customer</h2>
                    <p>ID: {obCustomer.obId}</p>
                    <p>Username: {obCustomer.username ?? obCustomer.obUsername}</p>
                </div>
            )}

            <div style={{marginBottom: '2rem'}}>
                <button className="button button-blue" onClick={handleConnectClick}>
                    Connect Your Accounts
                </button>
            </div>

            <div style={{marginBottom: '2rem'}}>
                <button
                    className="button button-blue"
                    style={{marginBottom: '1rem'}} 
                    onClick={handleRefreshAccountsClick}
                >
                    {obCustomerAccounts ? "Refresh" : "Get"} Connected Accounts
                </button>

                {isFetchingAccounts && <LoadingSpinner />}

                {!isFetchingAccounts && obCustomerAccounts && obCustomerAccounts.length > 0 && (
                    <>
                        <div>
                            <label>
                                Expand All
                                <input
                                    type="checkbox"
                                    checked={expandAll}
                                    onChange={() => setExpandAll(!expandAll)}
                                />
                            </label>
                        </div>

                        {obCustomerAccounts.map(account =>
                            <OBAccountPreview
                                account={account}
                                expandAll={expandAll}
                                key={account.id + account.name}
                            />
                        )}
                    </>
                )}
            </div>

            <div style={{marginBottom: '2rem'}}>
                <button
                    className="button button-blue"
                    style={{marginBottom: '1rem'}}
                    onClick={handleGetTransactionsClick}
                >
                    {customerTransactions ? "Refresh" : "Get"} Transactions
                </button>

                {isFetchingTransactions && <LoadingSpinner />}
                {!isFetchingTransactions && customerTransactions && customerTransactions.length > 0 && <OpenBankingTransactions transactions={customerTransactions} />}
            </div>
        </>
    );
}

export default OpenBankingIntegration;
